import { useEffect, useState } from 'react';
import Select, { type MultiValue } from 'react-select';
import FormGroup from '../../generic/FormGroup';
import { getAllPlayerNames } from '../../../sources/player';
import { getRuntimes } from '../../../sources/runtime';
import Toggle from '../../inputs/Toggle';
import translations from '../../../translations';
import { getAllThemeNames } from '../../../sources/theme';
import { isNullOrEmpty } from '../../../utils/guard';
import CopyableText from '../../generic/CopyableText';
import type { Environment } from '../../../types/environment';
import type { RuntimeListResponse } from '../../../types/runtime';
import { useTenant } from '../../TenantProvider';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { AlertBannerType, ExAlertBanner } from '@boomi/exosphere';

type Props = {
    environment: Environment | null;
    updateEnvironment: (environment: Environment) => void;
    hasSubmitted: boolean;
};

const EnvironmentSettings = ({ environment, updateEnvironment, hasSubmitted }: Props) => {
    const dispatchNotification = useNotificationDispatch();
    const tenant = useTenant();
    const [players, setPlayers] = useState<string[]>([]);
    const [themes, setThemes] = useState<string[]>([]);
    const [localRuntimes, setLocalRuntimes] = useState<RuntimeListResponse[]>([]);
    const [selectedRuntimes, setSelectedRuntimes] = useState<string[]>([]);

    const [isLoadingRuntimes, setIsLoadingRuntimes] = useState(true);

    const isInOrg = tenant.organization !== null;
    const isOrgAdmin = tenant.organization?.role === 'organization_administrator';

    const onDataChange = (name: string, value: string | boolean | string[]) => {
        if (environment) {
            updateEnvironment({
                ...environment,
                [name]: value,
            });
        }
    };

    // Helper functions for runtime selection
    const getRuntimeOptions = () => {
        return localRuntimes.map((runtime) => ({
            value: runtime.id,
            label: runtime.developerName,
        }));
    };

    const onRuntimeSelectionChange = (
        selectedOptions: MultiValue<{ value: string; label: string }>,
    ) => {
        const runtimeIds = selectedOptions.map((option) => option.value);
        setSelectedRuntimes(runtimeIds);
        onDataChange('runtimeIds', runtimeIds);
    };

    const canDisplayLocalRuntimesSelect = isInOrg && isOrgAdmin && localRuntimes.length > 0;

    useEffect(() => {
        const loadPlayersThemesAndRuntimes = async () => {
            try {
                const players = await getAllPlayerNames();
                const themes = await getAllThemeNames();

                setPlayers(players);
                setThemes(themes);

                if (isOrgAdmin) {
                    const runtimes = await getRuntimes();
                    setLocalRuntimes(runtimes);
                    setIsLoadingRuntimes(false);
                }
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        };

        loadPlayersThemesAndRuntimes();
    }, [dispatchNotification, isOrgAdmin]);

    // Update selectedRuntimes when environment or localRuntimes change
    useEffect(() => {
        if (environment?.runtimeIds && localRuntimes.length > 0) {
            // Only keep runtime IDs that exist in localRuntimes
            const validRuntimeIds = environment.runtimeIds.filter((id) =>
                localRuntimes.some((runtime) => runtime.id === id),
            );
            setSelectedRuntimes(validRuntimeIds);
        } else {
            setSelectedRuntimes([]);
        }
    }, [environment, localRuntimes]);

    return (
        <div className="margin-top">
            {!isInOrg && (
                <div className="margin-bottom">
                    <ExAlertBanner type={AlertBannerType.INFO} open>
                        {translations.ENVIRONMENT_no_org}
                    </ExAlertBanner>
                </div>
            )}

            {isInOrg && !isOrgAdmin && (
                <div className="margin-bottom">
                    <ExAlertBanner type={AlertBannerType.INFO} open>
                        {translations.ENVIRONMENT_no_org_admin}
                    </ExAlertBanner>
                </div>
            )}
            <FormGroup
                label="Environment Name:"
                htmlFor="environment-name"
                isRequired={true}
                validationMessage="Environment Name field is required"
                isValid={!isNullOrEmpty(environment?.name)}
                showValidation={hasSubmitted}
            >
                <input
                    id="environment-name"
                    className="form-control form-control-dynamic"
                    value={environment?.name}
                    type="text"
                    onChange={(e) => onDataChange('name', e.target.value)}
                />
            </FormGroup>
            <FormGroup label="Description:" htmlFor="environment-description" isRequired={false}>
                <textarea
                    id="environment-description"
                    className="form-control form-control-textarea"
                    value={environment?.description}
                    onChange={(e) => onDataChange('description', e.target.value)}
                    rows={3}
                />
            </FormGroup>
            {!tenant.tenantSettings?.themes && (
                <FormGroup
                    label="Select a default player"
                    htmlFor="default-player"
                    isRequired
                    validationMessage="Default player is required"
                    isValid={!isNullOrEmpty(environment?.defaultPlayerName)}
                    showValidation={hasSubmitted}
                >
                    <select
                        id="default-player"
                        className="form-control form-control-dynamic"
                        value={environment?.defaultPlayerName}
                        onChange={(e) => onDataChange('defaultPlayerName', e.target.value)}
                    >
                        <option key="-1" value="">
                            Select player
                        </option>
                        {players
                            ? players.map((player) => (
                                  <option key={player} value={player}>
                                      {player}
                                  </option>
                              ))
                            : null}
                    </select>
                </FormGroup>
            )}
            {tenant.tenantSettings?.themes && (
                <FormGroup
                    label="Select a default theme"
                    htmlFor="default-theme"
                    isRequired
                    validationMessage="Default theme is required"
                    isValid={!isNullOrEmpty(environment?.defaultThemeName)}
                    showValidation={hasSubmitted}
                >
                    <select
                        id="default-theme"
                        className="form-control form-control-dynamic"
                        value={environment?.defaultThemeName}
                        onChange={(e) => onDataChange('defaultThemeName', e.target.value)}
                    >
                        <option key="-1" value="">
                            Select theme
                        </option>
                        {themes?.map((theme) => (
                            <option key={theme} value={theme}>
                                {theme}
                            </option>
                        ))}
                    </select>
                </FormGroup>
            )}

            {canDisplayLocalRuntimesSelect && (
                <FormGroup
                    label="Select Local Runtimes:"
                    htmlFor="local-runtimes"
                    isRequired={false}
                >
                    <Select
                        id="local-runtimes"
                        isMulti={true}
                        options={getRuntimeOptions()}
                        value={selectedRuntimes
                            .map((id) => {
                                const runtime = localRuntimes.find((r) => r.id === id);
                                return runtime ? { value: id, label: runtime.developerName } : null;
                            })
                            .filter(
                                (option): option is { value: string; label: string } =>
                                    option !== null,
                            )}
                        onChange={onRuntimeSelectionChange}
                        placeholder="Select local runtimes..."
                        isClearable={true}
                        isSearchable={true}
                        noOptionsMessage={() => 'No local runtimes available'}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isLoading={isLoadingRuntimes}
                        className="form-control-long"
                    />
                </FormGroup>
            )}

            <div className="form-group">
                <label htmlFor="is-default-toggle">
                    <Toggle
                        id="is-default-toggle"
                        isOn={!!environment?.isDefault}
                        onChange={({ isOn }) => onDataChange('isDefault', isOn)}
                        testId="is-default"
                    />
                    {translations.ENVIRONMENT_default_option}
                </label>
            </div>
            <CopyableText
                labelText="Environment ID:"
                copyableText={environment?.id || ''}
                hasCopyButton={true}
            />
        </div>
    );
};

export default EnvironmentSettings;
